import { type ReactNode, useRef } from "react";
import { Box, Button, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ImageIcon from "@mui/icons-material/Image";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { ResizableImage } from "@/components/email/ResizableImage";

// Matches Gmail::ReplySender::MAX_INLINE_IMAGE_BYTES; the server checks too.
export const MAX_INLINE_IMAGE_BYTES = 5 * 1024 * 1024;
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
// Fractions of the editor's width; null puts the image back at its own size.
const IMAGE_SIZES = [
  { label: "S", fraction: 0.25 },
  { label: "M", fraction: 0.5 },
  { label: "L", fraction: 0.75 },
  { label: "Full", fraction: 1 },
  { label: "Original", fraction: null },
] as const;

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// Inline images live in the editor as data URLs; the server turns them into
// cid: parts when the reply is sent.
async function insertImages(editor: Editor, files: File[], onError: (msg: string) => void, pos?: number) {
  for (const file of files) {
    if (!IMAGE_TYPES.includes(file.type)) continue;
    if (file.size > MAX_INLINE_IMAGE_BYTES) {
      onError(`${file.name} is over 5 MB. Attach it instead.`);
      continue;
    }
    const src = await readAsDataUrl(file);
    const chain = editor.chain().focus();
    (pos != null ? chain.insertContentAt(pos, { type: "image", attrs: { src, alt: file.name } }) : chain.setImage({ src, alt: file.name })).run();
  }
}

function ToolButton({ title, active, disabled, onClick, children }: {
  title: string; active?: boolean; disabled?: boolean; onClick: () => void; children: ReactNode;
}) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()} // keep the editor's selection
          onClick={onClick}
          color={active ? "primary" : "default"}
          sx={{ borderRadius: 1, ...(active ? { bgcolor: "action.selected" } : {}) }}
          aria-label={title}
          aria-pressed={active}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export function RichTextEditor({ onChange, onError, onAttach, placeholder, minHeight = 180, autoFocus, fill, initialHtml }: {
  onChange: (html: string, isEmpty: boolean) => void;
  onError: (message: string) => void;
  // Files dropped on the editor that aren't images go here (as attachments).
  onAttach?: (files: File[]) => void;
  placeholder?: string;
  minHeight?: number;
  autoFocus?: boolean;
  // Grow to fill the parent (the full-screen composer).
  fill?: boolean;
  // Starting content (e.g. a signature being edited). Read once, on mount.
  initialHtml?: string;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onAttachRef = useRef(onAttach);
  onAttachRef.current = onAttach;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      ResizableImage.configure({ inline: false, allowBase64: true }), // images sit on their own line
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: placeholder ?? "Write your reply…" }),
    ],
    content: initialHtml ?? "",
    autofocus: autoFocus ? "end" : false,
    onUpdate: ({ editor: e }) => onChange(e.getHTML(), e.isEmpty),
    editorProps: {
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []).filter((f) => IMAGE_TYPES.includes(f.type));
        if (files.length === 0 || !editorRef.current) return false;
        event.preventDefault();
        void insertImages(editorRef.current, files, onErrorRef.current);
        return true;
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved || !editorRef.current) return false;
        const dropped = Array.from(event.dataTransfer?.files ?? []);
        if (dropped.length === 0) return false;
        event.preventDefault();
        const images = dropped.filter((f) => IMAGE_TYPES.includes(f.type));
        const others = dropped.filter((f) => !IMAGE_TYPES.includes(f.type));
        if (others.length > 0) onAttachRef.current?.(others);
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        void insertImages(editorRef.current, images, onErrorRef.current, pos);
        return true;
      },
    },
  });
  const editorRef = useRef<Editor | null>(null);
  editorRef.current = editor;

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link address", previous ?? "https://");
    if (url === null) return;
    if (url.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  return (
    <Box
      sx={{
        border: 1, borderColor: "divider", borderRadius: 1, "&:focus-within": { borderColor: "primary.main" },
        ...(fill ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 } : {}),
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.25, px: 0.5, py: 0.25, borderBottom: 1, borderColor: "divider", bgcolor: "grey.50" }}>
        <ToolButton title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><FormatBoldIcon fontSize="small" /></ToolButton>
        <ToolButton title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><FormatItalicIcon fontSize="small" /></ToolButton>
        <ToolButton title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><FormatUnderlinedIcon fontSize="small" /></ToolButton>
        <ToolButton title="Strikethrough" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><StrikethroughSIcon fontSize="small" /></ToolButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolButton title="Bulleted list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><FormatListBulletedIcon fontSize="small" /></ToolButton>
        <ToolButton title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><FormatListNumberedIcon fontSize="small" /></ToolButton>
        <ToolButton title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><FormatQuoteIcon fontSize="small" /></ToolButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolButton title="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><FormatAlignLeftIcon fontSize="small" /></ToolButton>
        <ToolButton title="Align center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><FormatAlignCenterIcon fontSize="small" /></ToolButton>
        <ToolButton title="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><FormatAlignRightIcon fontSize="small" /></ToolButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolButton title="Link" active={editor.isActive("link")} onClick={setLink}><InsertLinkIcon fontSize="small" /></ToolButton>
        <ToolButton title="Remove link" disabled={!editor.isActive("link")} onClick={() => editor.chain().focus().unsetLink().run()}><LinkOffIcon fontSize="small" /></ToolButton>
        <ToolButton title="Insert image" onClick={() => fileInput.current?.click()}><ImageIcon fontSize="small" /></ToolButton>
        {/* Size presets for the selected image (it can also be dragged from its corner). */}
        {editor.isActive("image") && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>Image size</Typography>
            {IMAGE_SIZES.map(({ label, fraction }) => (
              <Button
                key={label}
                size="small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const width = fraction ? Math.round((editor.view.dom as HTMLElement).clientWidth * fraction) : null;
                  editor.chain().focus().updateAttributes("image", { width }).run();
                }}
                sx={{ minWidth: 0, px: 1 }}
              >
                {label}
              </Button>
            ))}
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <ToolButton title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><UndoIcon fontSize="small" /></ToolButton>
        <ToolButton title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><RedoIcon fontSize="small" /></ToolButton>
        <input
          ref={fileInput}
          type="file"
          accept={IMAGE_TYPES.join(",")}
          multiple
          hidden
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = "";
            void insertImages(editor, files, onError);
          }}
        />
      </Box>
      <Box
        sx={{
          px: 1.5, py: 1, cursor: "text", fontSize: 14, lineHeight: 1.6,
          ...(fill ? { flex: 1, overflow: "auto" } : {}),
          "& .ProseMirror": { minHeight, outline: "none" },
          "& .ProseMirror p": { my: 0.5 },
          "& .ProseMirror img": { maxWidth: "100%", height: "auto", borderRadius: 0.5 },
          "& .ProseMirror .ProseMirror-selectednode img": { outline: "2px solid", outlineColor: "primary.main" },
          "& .ProseMirror blockquote": { borderLeft: "3px solid", borderColor: "divider", ml: 0, pl: 1.5, color: "text.secondary" },
          "& .ProseMirror a": { color: "primary.main" },
          "& .ProseMirror p.is-editor-empty:first-of-type::before": {
            content: "attr(data-placeholder)", color: "text.disabled", float: "left", height: 0, pointerEvents: "none",
          },
        }}
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
